import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  EmptyBox,
  Pagination,
  QueryBoundary,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';
import { useAccounts } from '../hooks/accounts';
import { dateTime, num, signedMoney } from '../lib/format';

const LIMIT = 50;

export default function Positions() {
  const { selectedId, isLoading: accountsLoading } = useAccounts();
  const [symbol, setSymbol] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ['positions', selectedId, symbol, openOnly, offset],
    queryFn: () =>
      api.positions({
        account_id: selectedId as number,
        symbol: symbol || undefined,
        open_only: openOnly,
        limit: LIMIT,
        offset,
      }),
    enabled: selectedId != null,
  });

  if (accountsLoading) return <Spinner />;
  const page = q.data;

  return (
    <>
      <PageHeader
        title="Positions"
        description="Open and historical positions for the selected account."
      />

      {selectedId == null ? (
        <EmptyBox>No account selected. Configure an account row first (see Operations).</EmptyBox>
      ) : (
       <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={symbol}
          onChange={(e) => {
            setSymbol(e.target.value.toUpperCase());
            setOffset(0);
          }}
          placeholder="Symbol filter (e.g. EURUSD)"
          className="rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
        />
        <label className="flex items-center gap-1.5 text-sm text-slate-400">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => {
              setOpenOnly(e.target.checked);
              setOffset(0);
            }}
          />
          Open only
        </label>
      </div>

      <QueryBoundary q={q} isEmpty={page?.items.length === 0} empty={<p className="text-sm text-slate-500">No positions match.</p>}>
        {page && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Ticket</Th>
                  <Th>Symbol</Th>
                  <Th>Side</Th>
                  <Th className="text-right">Volume</Th>
                  <Th className="text-right">Open</Th>
                  <Th>Opened</Th>
                  <Th>Closed</Th>
                  <Th className="text-right">Net P&L</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {page.items.map((p) => (
                  <tr key={p.ticket} className="hover:bg-slate-800/30">
                    <Td className="tabular-nums">{p.ticket}</Td>
                    <Td>{p.symbol}</Td>
                    <Td>
                      <Badge tone={p.direction === 'long' ? 'good' : 'bad'}>{p.direction}</Badge>
                      {p.is_external && <span className="ml-1 text-xs text-amber-400">ext</span>}
                    </Td>
                    <Td className="text-right tabular-nums">{num(p.volume, 2)}</Td>
                    <Td className="text-right tabular-nums">{num(p.open_price, 5)}</Td>
                    <Td className="whitespace-nowrap text-xs text-slate-400">{dateTime(p.open_time_utc)}</Td>
                    <Td className="whitespace-nowrap text-xs text-slate-400">
                      {p.close_time_utc ? dateTime(p.close_time_utc) : <Badge tone="info">open</Badge>}
                    </Td>
                    <Td
                      className={`text-right tabular-nums ${
                        p.net_profit != null && Number(p.net_profit) < 0
                          ? 'text-red-400'
                          : p.net_profit != null && Number(p.net_profit) > 0
                            ? 'text-emerald-400'
                            : ''
                      }`}
                    >
                      {p.net_profit != null ? signedMoney(p.net_profit) : '—'}
                    </Td>
                    <Td>
                      <Link className="text-sky-400 hover:underline" to={`/positions/${p.ticket}`}>
                        trace →
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination total={page.total} limit={LIMIT} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>
      </>
      )}
    </>
  );
}
